<template>
    <div class="pb-20">
        <div class="container relative">
            <SectionIntro
                :title="$t('products.reprint_categories')"
                :subtitle="$t('products.browse_favourites_subtitle')"
                class="mt-14 lg:mt-14"
                xl
            />
            <div class="grid grid-cols-12 gap-4 mt-8">
                <nuxt-link
                    v-if="highlightedCategories && highlightedCategories.length"
                    :to="
                        localePath({
                            name: 'products-category',
                            params: { category: mainCategory.translations[0].slug },
                        })
                    "
                    class="col-span-12 lg:col-span-8"
                >
                    <div class="sm:rounded-md sm:ml-0 sm:w-auto rounded-lg lg:overflow-hidden featured-product-cart">
                        <div
                            class="aspect-w-16 aspect-h-5 sm:aspect-w-16 sm:aspect-h-11 md:aspect-w-16 md:aspect-h-9 rounded-lg border border-black border-opacity-20 lg:border-none"
                        >
                            <div class="p-4 lg:p-0 flex items-center justify-between lg:block h-full w-full">
                                <div class="h-full">
                                    <div
                                        class="lg:absolute text-black lg:text-white top-8 lg:top-14 left-6 lg:left-14 z-10 lg:w-3/5 lg:pr-12"
                                    >
                                        <h2 class="font-Inter-Bold text-lg md:text-2xl xl:text-3xl">
                                            {{ mainCategory.translations[0].name }}
                                        </h2>
                                        <h3 class="font-Inter-Regular text-sm lg:leading-6 lg:text-lg mt-4">
                                            {{
                                                $device.isDesktop
                                                    ? mainCategory.translations[0].description
                                                    : $t('products.view_more')
                                            }}
                                        </h3>
                                        <PButton
                                            class="mt-8 hidden lg:flex"
                                            :label="$t('products.view')"
                                            appearance="secondary"
                                        />
                                    </div>
                                    <app-image
                                        v-if="mainCategory?.image"
                                        class="object-cover h-full w-full cover-image hidden lg:block"
                                        :src="mainCategory.image"
                                        :alt="mainCategory.translations[0].name"
                                        preset="banner"
                                        eager
                                    />
                                </div>
                                <div
                                    class="w-8 h-8 min-w-[2rem] min-h-[2rem] max-w-8 max-h-8 flex lg:hidden items-center justify-center rounded-full text-white bg-blue-100"
                                >
                                    <i class="ri-arrow-right-s-line leading-none"></i>
                                </div>
                            </div>
                        </div>
                    </div>
                </nuxt-link>
                <div v-if="$device.isDesktop" class="col-span-12 lg:col-span-4">
                    <div
                        v-if="banners"
                        class="rounded-lg overflow-hidden aspect-w-16 aspect-h-9 lg:aspect-w-4 lg:aspect-h-3 relative h-full banner-card hidden lg:block"
                    >
                        <nuxt-link :to="banners.link">
                            <div class="lg:absolute lg:text-white top-6 left-6 z-10">
                                <h2
                                    class="font-Inter-Bold text-xl lg:text-2xl"
                                    :class="banners.name === 'category-page' ? 'w-full' : 'lg:w-1/2'"
                                >
                                    {{ banners?.title_hu }}
                                </h2>
                                <span class="mt-3 font-Inter-Regular text-sm category-link"
                                    >{{ $t('products.view_more') }}</span
                                >
                            </div>
                            <app-image
                                v-if="banners?.image"
                                class="object-cover h-full w-full absolute top-0 left-0 cover-image hidden lg:block"
                                :src="banners.image"
                                alt=""
                                preset="tile"
                                eager
                            />
                        </nuxt-link>
                    </div>
                </div>
                <div
                    v-for="category in highlightedCategories"
                    :key="`category-${category.id}`"
                    class="col-span-12 lg:col-span-4"
                >
                    <nuxt-link
                        v-if="category?.image"
                        :to="
                            localePath({
                                name: 'products-category',
                                params: { category: category.translations[0].slug },
                            })
                        "
                        class="aspect-w-16 aspect-h-5 lg:aspect-w-4 lg:aspect-h-3 border border-black border-opacity-20 lg:border-none rounded-lg overflow-hidden relative category-card w-full"
                    >
                        <div class="p-4 lg:p-0 flex items-center justify-between lg:block h-full w-full">
                            <div class="lg:h-full">
                                <div class="lg:absolute text-black lg:text-white top-6 left-6 z-10">
                                    <h2 class="font-Inter-Bold text-lg lg:text-[1.5rem]">
                                        {{ category.translations[0].name }}
                                    </h2>
                                    <span class="mt-2 lg:mt-3 font-Inter-Regular text-sm category-link"
                                        >{{ $t('products.view_more') }}</span
                                    >
                                </div>
                                <app-image
                                    v-if="category?.image"
                                    class="object-cover h-full w-full hidden lg:block"
                                    :src="category.image"
                                    :alt="category.translations[0].name"
                                    preset="tile"
                                />
                            </div>
                            <div
                                class="w-8 h-8 min-w-[2rem] min-h-[2rem] max-w-8 max-h-8 flex lg:hidden items-center justify-center rounded-full text-white bg-blue-100"
                            >
                                <i class="ri-arrow-right-s-line leading-none"></i>
                            </div>
                        </div>
                    </nuxt-link>
                </div>
            </div>
            <SectionIntro
                :title="$t('products.pictures.all_reprints')"
                :subtitle="$t('products.browse_favourites_subtitle')"
                class="mt-14 lg:mt-20"
                xl
            />
            <button
                v-if="$device.isMobile"
                class="whitespace-nowrap flex items-center justify-center w-full lg:w-auto font-Inter-Bold bg-white border border-black py-1 px-5 rounded-full transition text-black"
                @click="showFilters()"
            >
                <i class="text-xl mr-2" :class="isFiltersVisible ? 'ri-close-line' : 'ri-filter-3-line'"></i
                ><span>{{ $t('products.filters.show') }}</span>
            </button>
            <transition name="fade">
                <div
                    v-if="isFiltersVisible || $device.isDesktop"
                    class="grid grid-cols-12 mt-8 lg:gap-x-8 gap-y-4 lg:gap-y-0 pb-6 border-b border-black border-opacity-5 mb-8"
                >
                    <div class="col-span-12 lg:col-span-4">
                        <Input
                            v-model="filters.searchText"
                            :label="$t('products.filters.search_label')"
                            :placeholder="$t('products.filters.search_placeholder')"
                            @input="updateQueries"
                        />
                    </div>
                    <div class="col-span-12 lg:col-span-4">
                        <Input
                            v-model="filters.orientation"
                            :label="$t('products.filters.orientation_label')"
                            :placeholder="$t('products.filters.orientation_placeholder')"
                            has-clear-btn
                            :options="orientation"
                            @input="updateQueries"
                        />
                    </div>
                    <div class="col-span-12 lg:col-span-4">
                        <Input
                            v-model="filters.arrangement"
                            :label="$t('products.filters.sort_label')"
                            :placeholder="$t('products.filters.sort_placeholder')"
                            has-clear-btn
                            :options="arrangement"
                            @input="updateQueries"
                        />
                    </div>
                </div>
            </transition>
            <div v-if="(products && products.length) || processedProducts" class="mt-10">
                <ProductGrid :products="processedProducts || products" :product-count="10" />
            </div>
            <div v-else class="text-center mb-12 lg:mb-0 flex flex-col items-center justify-center h-full mt-24">
                <i class="sad-emoji mb-3" data-not-lazy></i>
                <h4 class="font-Inter-Bold text-xl lg:text-[1.5rem] mb-4 leading-[2rem]" v-html="$t('products.no_results_title')"></h4>
                <p class="lg:w-8/12 text-sm lg:text-base">
                    {{ $t('products.no_results_text') }}
                </p>
            </div>
            <ScrollToTopButton />
        </div>
    </div>
</template>

<script>
import { debounce } from 'vue-debounce';
import ProductGrid from '~/components/Layout/ProductGrid.vue';
import ScrollToTopButton from '@/components/UI/Buttons/ScrollToTopButton.vue';
export default {
    name: 'ProductFilter',
    components: {
        ProductGrid,
        ScrollToTopButton,
    },
    async asyncData({ $axios, query }) {
        let products = [];
        let categories = [];
        let highlightedCategories = [];
        let featuredOffers = [];
        let highlightedProducts = [];
        let banners = [];
        let mainCategory = null;

        const queryParams = {
            search: query.search,
            orientation: query.orientation,
            order: query.order,
        };

        try {
            await Promise.allSettled([
                $axios.$get(`/products`, { params: queryParams }),
                $axios.$get(`/categories`),
                $axios.$get(`/highlighted-categories`),
                $axios.$get('/featured-offers'),
                $axios.$get('/highlighted-products'),
                $axios.$get(`/banners`),
            ])
                .then(
                    ([
                        productRes,
                        categoryRes,
                        highlightedCategoriesRes,
                        featuredOffersRes,
                        highlightedProductsRes,
                        bannersRes,
                    ]) => {
                        if (productRes.status === 'fulfilled' && productRes.value.result === 'success') {
                            products = productRes.value.data.filter(
                                (product) => product.category_name !== 'Whispers of Wood'
                            );
                        }
                        if (categoryRes.status === 'fulfilled' && categoryRes.value.result === 'success') {
                            categories = categoryRes.value.data;
                            mainCategory = categoryRes.value.data.find((category) => category?.main === 1);
                        }
                        if (
                            highlightedCategoriesRes.status === 'fulfilled' &&
                            highlightedCategoriesRes.value.result === 'success'
                        ) {
                            highlightedCategories = highlightedCategoriesRes.value.data;
                        }
                        if (featuredOffersRes.status === 'fulfilled' && featuredOffersRes.value?.result === 'success') {
                            featuredOffers = featuredOffersRes.value.data;
                        }
                        if (
                            highlightedProductsRes.status === 'fulfilled' &&
                            highlightedProductsRes.value.result === 'success'
                        ) {
                            highlightedProducts = highlightedProductsRes.value.data;
                        }
                        if (bannersRes.status === 'fulfilled' && bannersRes.value.result === 'success') {
                            banners = bannersRes.value.data.find((position) => position.name === 'category-page');
                        }
                    }
                )
                .catch((err) => {
                    console.log(err);
                });
        } catch (err) {
            console.log(err);
        }

        return {
            products,
            categories,
            highlightedCategories,
            featuredOffers,
            highlightedProducts,
            banners,
            mainCategory,
        };
    },
    data() {
        return {
            categories: null,
            products: null,
            processedProducts: null,
            productCount: 10,
            resultsCount: 200,
            selectedCategory: null,
            highlightedCategories: null,
            isFiltersVisible: false,
            filters: {
                searchText: '',
                brand: null,
                orientation: null,
                arrangement: null,
            },
            selectedBrand: null,
        };
    },
    head() {
        const meta = [
            {
                hid: 'og:title',
                property: 'og:title',
                content: this.$t('footer.gallery'),
            },
            {
                hid: 'title',
                name: 'title',
                content: this.$t('products.meta.posters_title'),
            },
            {
                hid: 'og:description',
                property: 'og:description',
                content: this.$t('products.browse_favourites_subtitle'),
            },
            {
                hid: 'description',
                name: 'description',
                content: this.$t('products.browse_favourites_subtitle'),
            },
        ];
        return {
            title: this.$t('footer.gallery'),
            meta,
        };
    },
    computed: {
        arrangement() {
            return [
                { id: 'SORT_ASC', name: this.$t('products.filters.sort_oldest') },
                { id: 'SORT_DESC', name: this.$t('products.filters.sort_newest') },
            ];
        },
        orientation() {
            return [
                { id: 'vertical', name: this.$t('products.filters.orientation_vertical') },
                { id: 'horizontal', name: this.$t('products.filters.orientation_horizontal') },
            ];
        },
    },
    watch: {
        $route(from, to) {
            this.filterProducts(from.query, from.params);
        },
    },
    beforeMount() {
        this.filters.searchText = this.$route.query.search;

        if (this.$route.query.orientation) {
            this.filters.orientation = this.$route.query.orientation;
        }
        if (this.$route.query.order) {
            this.filters.arrangement = this.$route.query.order;
        }
    },
    methods: {
        loadMoreProducts() {
            if (this.processedProducts.length > this.productCount) {
                this.productCount = this.productCount + 5;
            }
        },
        scrollBasedOnPosition(event) {
            const container = this.$refs.scrollableContainer;
            if (!container) return;

            const containerRect = container.getBoundingClientRect(); // Container's bounding box
            const itemRect = event.target.getBoundingClientRect(); // Clicked item's bounding box

            const itemPosition = itemRect.left - containerRect.left; // Distance from the container's left edge
            const containerWidth = containerRect.width;

            if (itemPosition < containerWidth / 2) {
                container.scrollTo({
                    left: 0,
                    behavior: 'smooth',
                });
            } else {
                container.scrollTo({
                    left: container.scrollWidth,
                    behavior: 'smooth',
                });
            }
        },
        selectCategory(currentCategory, event) {
            this.processedProducts = this.products.filter((product) => {
                return product.category_id === parseInt(currentCategory);
            });
            this.selectedCategory = currentCategory;
            this.scrollBasedOnPosition(event);
        },
        isActiveTab(tabId) {
            return this.selectedCategory === tabId;
        },
        resetProducts() {
            this.processedProducts = this.products;
            this.selectedCategory = null;
        },
        showFilters() {
            this.isFiltersVisible = !this.isFiltersVisible;
        },
        updateQueries: debounce(function () {
            this.$router.push({
                path: this.$route.path,
                query: {
                    search: this.filters.searchText,
                    orientation: this.filters.orientation,
                    order: this.filters.arrangement,
                },
            });
        }, 250),
        filterProducts(query, params) {
            const queryParams = {
                search: query.search,
                orientation: query.orientation,
                order: query.order,
            };

            this.$axios
                .get(`/products`, { params: queryParams })
                .then((response) => {
                    this.processedProducts = response.data.data;
                })
                .catch((err) => {
                    console.log(err);
                });
        },
    },
};
</script>

<style>
.category-navigation {
    @apply relative;
    &::after {
        content: '';
        @apply absolute h-full w-8 block right-0 top-0 pointer-events-none;
        background: linear-gradient(270deg, #fdfdfd 0%, rgba(253, 253, 253, 0) 100%);
    }
    &::before {
        content: '';
        @apply absolute h-full w-8 block -left-4 lg:-left-8 top-0 pointer-events-none rotate-180;
        background: linear-gradient(270deg, #fdfdfd 0%, rgba(253, 253, 253, 0) 100%);
    }
}
.category-link {
    @apply flex items-center;

    &::after {
        content: '';
        @apply w-2 h-2 bg-no-repeat bg-center bg-contain ml-2 block transition-all mt-[0.1rem];
        background-image: url('~/assets/images/icons/chevron-right-white.svg');
    }
}
.category-card {
    img {
        @apply transition-all;
    }

    &::before {
        content: '';
        @apply absolute top-0 left-0 w-4/5 h-full hidden lg:block pointer-events-none z-1 opacity-0 lg:opacity-100;
        background: linear-gradient(90deg, rgba(0, 0, 0, 0.5) 0%, rgba(0, 0, 0, 0) 100%);
    }

    &:hover {
        .category-link {
            &::after {
                @apply translate-x-1;
            }
        }

        img {
            @apply scale-105;
        }
    }
}
.banner-card {
    .cover-image {
        @apply transition-all;
    }
    &::before {
        content: '';
        @apply absolute top-0 left-0 w-full h-1/2 hidden lg:block pointer-events-none z-1;
        background: linear-gradient(180deg, rgba(0, 0, 0, 0.3) 0%, rgba(0, 0, 0, 0) 100%);
    }
    &:hover {
        .cover-image {
            @apply scale-[103%];
        }
    }
}
.featured-product-cart {
    .cover-image {
        @apply transition-all;
    }
    &:hover {
        .cover-image {
            @apply scale-[103%];
        }
    }
}

.fade-out-r {
    &::before {
        content: '';
        @apply absolute right-0 top-0 w-8 h-full pointer-events-none;
        background: linear-gradient(270deg, #fff 0%, rgba(255, 255, 255, 0) 100%);
    }
}
</style>
